import {
  Button,
  Checkbox,
  Column,
  Content,
  Grid,
  InlineNotification,
  Section,
  TextInput,
  Tile,
} from '@carbon/react';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { type SEOHandle } from '@nasa-gcn/remix-seo';
import { startAuthentication } from '@simplewebauthn/browser';
import { useOptimistic, useState, useTransition } from 'react';
import { data, Form, Link, useNavigate, useSearchParams } from 'react-router';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';

import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
// import { ErrorList } from '#app/components/forms.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { login, requireAnonymous } from '#app/utils/auth.server.ts';
// import {
//   ProviderConnectionForm,
//   providerNames,
// } from '#app/utils/connections.tsx';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx';
import { getSystemData } from '#app/utils/shared-auth.server.ts';
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts';
import { type Route } from './+types/login.ts';
import { handleNewSession } from './login.server.ts';

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
};

const LoginFormSchema = z.object({
  username: UsernameSchema,
  password: PasswordSchema,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
});

// const AuthenticationOptionsSchema = z.object({
//   options: z.object({ challenge: z.string() }),
// });

export async function loader({ request }: Route.LoaderArgs) {
  await requireAnonymous(request);
  const { systemName } = await getSystemData();
  return { systemName };
}

export async function action({ request }: Route.ActionArgs) {
  await requireAnonymous(request);
  const formData = await request.formData();

  await checkHoneypot(formData);
  const submission = await parseWithZod(formData, {
    schema: (intent) =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null };

        try {
          const session = await login(data);
          if (!session) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Invalid username or password',
            });
            return z.NEVER;
          }

          return { ...data, session };
        } catch (error) {
          if (error instanceof Error) {
            if (error.message === 'ACCOUNT_PENDING') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Your account is pending activation. Please check your email for the activation link or contact support for assistance.',
              });
              return z.NEVER;
            }
            if (error.message === 'ACCOUNT_SUSPENDED') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Your account has been suspended. Please contact support for assistance.',
              });
              return z.NEVER;
            }
            if (error.message === 'ACCOUNT_LOCKED') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Your account has been locked. Please contact support for assistance.',
              });
              return z.NEVER;
            }
            if (error.message === 'ACCOUNT_LOCKED_DUE_TO_FAILED_ATTEMPTS') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Your account has been locked due to multiple failed login attempts. Please try again in 30 minutes or contact support.',
              });
              return z.NEVER;
            }
          }
          // Re-throw other errors
          throw error;
        }
      }),
    async: true,
  });

  if (submission.status !== 'success' || !submission.value.session) {
    return data(
      { result: submission.reply({ hideFields: ['password'] }) },
      { status: submission.status === 'error' ? 400 : 200 },
    );
  }

  const { session, remember, redirectTo } = submission.value;

  return handleNewSession({
    request,
    session,
    remember: remember ?? false,
    redirectTo,
  });
}

export default function LoginPage({
  actionData,
  loaderData,
}: Route.ComponentProps) {
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  const message = searchParams.get('message');
  const { systemName } = loaderData;

  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: { redirectTo },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginFormSchema });
    },
    shouldRevalidate: 'onBlur',
  });

  return (
    <>
      <title>{`Login to ${systemName}`}</title>
      <Content className="min-h-screen bg-gray-900 !mt-0">
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Section className="flex  justify-center mt-52">
              <div className="mx-auto w-full max-w-md">
                <Tile className="p-8">
                  <div className="flex flex-col gap-3 text-center mb-6">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <img
                        src="/img/cb-logo-black.png"
                        alt="CircuitBread Logo"
                        className="h-8 w-auto"
                      />
                      <span className="text-2xl font-bold text-white">
                        {systemName}
                      </span>
                    </div>
                    <p className="text-gray-400">Please enter your details.</p>
                  </div>

                  {message === 'account-suspended' && (
                    <div className="mb-4">
                      <InlineNotification
                        kind="error"
                        title="Account Suspended"
                        subtitle="Your account has been suspended. Please contact support for assistance."
                        hideCloseButton
                      />
                    </div>
                  )}

                  <Form method="POST" {...getFormProps(form)}>
                    <HoneypotInputs />
                    <div className="mb-4">
                      <TextInput
                        {...getInputProps(fields.username, { type: 'text' })}
                        id={fields.username.id}
                        labelText="Username"
                        placeholder="Enter your username"
                        autoFocus
                        autoComplete="username"
                        invalid={(fields.password.errors?.length ?? 0) > 0}
                        invalidText={fields.username.errors?.[0]}
                      />
                    </div>

                    <div className="mb-4">
                      <TextInput
                        {...getInputProps(fields.password, {
                          type: 'password',
                        })}
                        id={fields.password.id}
                        labelText="Password"
                        placeholder="Enter your password"
                        type="password"
                        autoComplete="current-password"
                        invalid={(fields.password.errors?.length ?? 0) > 0}
                        invalidText={fields.password.errors?.[0]}
                      />
                    </div>

                    <div className="flex justify-between items-center mb-4">
                      <Checkbox
                        {...getInputProps(fields.remember, {
                          type: 'checkbox',
                        })}
                        id={fields.remember.id}
                        labelText="Remember me"
                      />
                      <div>
                        <Link
                          to="/forgot-password"
                          className="text-sm font-semiboldx text-blue-400 hover:text-blue-300 underline-offset-4"
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </div>

                    <input
                      {...getInputProps(fields.redirectTo, { type: 'hidden' })}
                    />

                    {form.errors && form.errors.length > 0 && (
                      <div className="mb-4">
                        <InlineNotification
                          kind="error"
                          title="Error"
                          subtitle={form.errors[0]}
                          hideCloseButton
                        />
                      </div>
                    )}

                    <div className="mb-4 flex">
                      <Button
                        type="submit"
                        kind="primary"
                        // size="md"
                        disabled={isPending}
                        className="disabled:text-[#4E5A66] hover:disabled:text-[#4E5A66]"
                      >
                        {isPending ? 'Logging in...' : 'Log in'}
                      </Button>
                    </div>
                  </Form>
                  {/* <hr className="my-4 border-gray-600" />
                <div className="flex flex-col gap-5">
                  <PasskeyLogin
                    redirectTo={redirectTo}
                    remember={fields.remember.value === 'on'}
                  />
                </div>
                <hr className="my-4 border-gray-600" /> */}
                  {/* <ul className="flex flex-col gap-5">
                  {providerNames.map((providerName) => (
                    <li key={providerName}>
                      <ProviderConnectionForm
                        type="Login"
                        providerName={providerName}
                        redirectTo={redirectTo}
                      />
                    </li>
                  ))}
                </ul> */}
                  {/* <div className="flex items-center justify-center gap-2 pt-6">
                  <span className="text-gray-400">New here?</span>
                  <Link
                    to={
                      redirectTo
                        ? `/nexus?redirectTo=${encodeURIComponent(redirectTo)}`
                        : '/nexus'
                    }
                    className="text-blue-400 hover:text-blue-300 no-underline hover:underline"
                  >
                    Create an account
                  </Link>
                </div> */}
                </Tile>
              </div>
            </Section>
          </Column>
        </Grid>
      </Content>
    </>
  );
}

// const VerificationResponseSchema = z.discriminatedUnion('status', [
//   z.object({
//     status: z.literal('success'),
//     location: z.string(),
//   }),
//   z.object({
//     status: z.literal('error'),
//     error: z.string(),
//   }),
// ]);

// function PasskeyLogin({
//   redirectTo,
//   remember,
// }: {
//   redirectTo: string | null;
//   remember: boolean;
// }) {
//   const [isPending] = useTransition();
//   const [error, setError] = useState<string | null>(null);
//   const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
//     'Login with a passkey',
//   );
//   const navigate = useNavigate();

//   async function handlePasskeyLogin() {
//     try {
//       setPasskeyMessage('Generating Authentication Options');
//       // Get authentication options from the server
//       const optionsResponse = await fetch('/webauthn/authentication');
//       const json = await optionsResponse.json();
//       const { options } = AuthenticationOptionsSchema.parse(json);

//       setPasskeyMessage('Requesting your authorization');
//       const authResponse = await startAuthentication({ optionsJSON: options });
//       setPasskeyMessage('Verifying your passkey');

//       // Verify the authentication with the server
//       const verificationResponse = await fetch('/webauthn/authentication', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ authResponse, remember, redirectTo }),
//       });

//       const verificationJson = await verificationResponse.json().catch(() => ({
//         status: 'error',
//         error: 'Unknown error',
//       }));

//       const parsedResult =
//         VerificationResponseSchema.safeParse(verificationJson);
//       if (!parsedResult.success) {
//         throw new Error(parsedResult.error.message);
//       } else if (parsedResult.data.status === 'error') {
//         throw new Error(parsedResult.data.error);
//       }
//       const { location } = parsedResult.data;

//       setPasskeyMessage("You're logged in! Navigating...");
//       await navigate(location ?? '/');
//     } catch (e) {
//       const errorMessage = getErrorMessage(e);
//       setError(`Failed to authenticate with passkey: ${errorMessage}`);
//     }
//   }

//   return (
//     <form action={handlePasskeyLogin}>
//       <Button
//         id="passkey-login-button"
//         aria-describedby="passkey-login-button-error"
//         className="w-full"
//         kind={error ? 'danger' : 'secondary'}
//         size="md"
//         type="submit"
//         disabled={isPending}
//       >
//         <span className="inline-flex items-center gap-1.5">
//           <Icon name="radix/passkey" />
//           <span>{passkeyMessage}</span>
//         </span>
//       </Button>
//       {error && (
//         <div className="mt-2">
//           <InlineNotification
//             kind="error"
//             title="Error"
//             subtitle={error}
//             hideCloseButton
//           />
//         </div>
//       )}
//     </form>
//   );
// }

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
